import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import { Users, CheckCircle, XCircle, Clock, AlertTriangle, LogIn } from 'lucide-react';
import { clientActions } from '../../../actions/client';
import { authActions } from '../../../actions/auth';
import authSlice from '../../../utils/redux/slice/authSlice';
import { useTheme } from '../../../contexts/ThemeContext';

function InvitationScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { colors } = useTheme?.() || { colors: { text: '#111', textSecondary: '#666', surface: '#fff', border: '#e5e5e5' } };
  const isAuthenticated = useSelector(authSlice.selectors.isAuthenticated);
  const token = searchParams.get('token');

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [done, setDone] = useState(false);

  const fetchInvitation = useCallback(async () => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }
    try {
      const res = await clientActions.getInvitationByToken(token);
      setInvitation(res);
    } catch (err) {
      setError(err?.error || err?.message || 'Invitation not found');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvitation();
  }, [fetchInvitation]);

  // Check for stored token on mount (in case user was redirected to login)
  useEffect(() => {
    if (isAuthenticated) {
      const storedToken = localStorage.getItem('pending_invitation_token');
      if (storedToken && !token) {
        localStorage.removeItem('pending_invitation_token');
        navigate(`/invitation?token=${storedToken}`, { replace: true });
      }
    }
  }, [isAuthenticated, token, navigate]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      localStorage.setItem('pending_invitation_token', token);
      navigate('/');
      return;
    }
    setAccepting(true);
    try {
      await clientActions.acceptInvitation({ token });
      message.success('Invitation accepted! Your account is now a sub-account.');
      // Refresh auth state
      const verifyRes = await authActions.verifyToken();
      if (verifyRes?.user) {
        dispatch(authSlice.actions.setUser(verifyRes.user));
      }
      setDone(true);
      setTimeout(() => navigate('/account'), 1500);
    } catch (err) {
      message.error(err?.error || err?.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!isAuthenticated || !invitation) return;
    setRejecting(true);
    try {
      await clientActions.rejectInvitation(invitation.invitation_id);
      message.success('Invitation declined');
      setDone(true);
    } catch (err) {
      message.error(err?.error || err?.message || 'Failed to decline invitation');
    } finally {
      setRejecting(false);
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: colors.surface || '#f9fafb',
  };

  const cardStyle = {
    maxWidth: 440,
    width: '100%',
    background: colors.surface || '#fff',
    border: `1px solid ${colors.border || '#e5e5e5'}`,
    borderRadius: 16,
    padding: 32,
    textAlign: 'center',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Users size={22} color="#fff" />
          </div>
          <div style={{ fontSize: 16, color: colors.textSecondary }}>Loading invitation...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <AlertTriangle size={40} style={{ color: '#EF4444', marginBottom: 16 }} />
          <div style={{ fontSize: 18, fontWeight: 600, color: colors.text, marginBottom: 8 }}>Invitation Error</div>
          <div style={{ fontSize: 14, color: colors.textSecondary, marginBottom: 24 }}>{error}</div>
          <button
            onClick={() => navigate('/')}
            style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#6366F1', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <CheckCircle size={40} style={{ color: '#22C55E', marginBottom: 16 }} />
          <div style={{ fontSize: 18, fontWeight: 600, color: colors.text, marginBottom: 8 }}>Done!</div>
          <div style={{ fontSize: 14, color: colors.textSecondary }}>Redirecting to your account...</div>
        </div>
      </div>
    );
  }

  const isExpired = invitation?.status === 'expired';
  const isAlreadyHandled = ['accepted', 'rejected', 'cancelled'].includes(invitation?.status);

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ width: 56, height: 56, borderRadius: 14, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Users size={26} color="#fff" />
        </div>

        <div style={{ fontSize: 22, fontWeight: 700, color: colors.text, marginBottom: 6, letterSpacing: '-0.02em' }}>
          Sub-Account Invitation
        </div>

        <div style={{ fontSize: 15, color: colors.textSecondary, marginBottom: 24 }}>
          <strong style={{ color: colors.text }}>{invitation?.inviter_name}</strong>
          {' '}({invitation?.inviter_email}) has invited you to join as a sub-account.
        </div>

        {isExpired && (
          <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <Clock size={16} color="#F59E0B" />
              <span style={{ fontSize: 14, fontWeight: 500, color: '#F59E0B' }}>This invitation has expired</span>
            </div>
            <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 6 }}>
              Ask the sender to resend the invitation.
            </div>
          </div>
        )}

        {isAlreadyHandled && (
          <div style={{ padding: '12px 16px', borderRadius: 10, background: `${colors.text}04`, border: `1px solid ${colors.border}`, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: colors.textSecondary }}>
              This invitation has already been {invitation?.status}.
            </div>
          </div>
        )}

        {!isExpired && !isAlreadyHandled && (
          <>
            <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.12)', marginBottom: 24, textAlign: 'left' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, marginBottom: 6 }}>What this means:</div>
              <ul style={{ fontSize: 13, color: colors.textSecondary, margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>Your account will become a sub-account under {invitation?.inviter_name}</li>
                <li>Your existing balance and API keys will be preserved</li>
                <li>The main account can allocate additional funds to you</li>
                <li>Spending limits may be set by the main account</li>
              </ul>
            </div>

            {!isAuthenticated ? (
              <button
                onClick={() => {
                  localStorage.setItem('pending_invitation_token', token);
                  navigate('/');
                }}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  borderRadius: 10,
                  border: 'none',
                  background: '#6366F1',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 15,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <LogIn size={16} />
                Log In to Respond
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={handleReject}
                  disabled={rejecting}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: `1px solid ${colors.border}`,
                    background: 'transparent',
                    color: colors.textSecondary,
                    cursor: rejecting ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    opacity: rejecting ? 0.6 : 1,
                  }}
                >
                  <XCircle size={15} />
                  {rejecting ? 'Declining...' : 'Decline'}
                </button>
                <button
                  onClick={handleAccept}
                  disabled={accepting}
                  style={{
                    flex: 2,
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#6366F1',
                    color: '#fff',
                    cursor: accepting ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    opacity: accepting ? 0.6 : 1,
                  }}
                >
                  <CheckCircle size={15} />
                  {accepting ? 'Accepting...' : 'Accept Invitation'}
                </button>
              </div>
            )}
          </>
        )}

        {(isExpired || isAlreadyHandled) && (
          <button
            onClick={() => navigate(isAuthenticated ? '/account' : '/')}
            style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#6366F1', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
          >
            {isAuthenticated ? 'Go to Account' : 'Go to Login'}
          </button>
        )}
      </div>
    </div>
  );
}

export default InvitationScreen;
